import { MutableRefObject, ReactNode, RefObject } from 'react';

import {
  Alert,
  Anchor,
  Box,
  Card,
  CardProps,
  CheckboxProps,
  Collapse,
  Divider,
  Group,
  ImageMantine,
  RadioCard,
  RadioGroupProps,
  RadioMantine,
  Stack,
  Text,
} from 'ui';

import { ShopToGiveTermsCheckboxCard } from '../../molecules/ShopToGiveTermsCheckboxCard/ShopToGiveTermsCheckboxCard';
import classes from './ShopToGiveEnrollmentForm.module.css';
import image from './ShopToGiveEnrollmentFormImage.png';

const ShopToGiveEnrollmentForm = ({
  causeName,
  scrollerRef,
  containerProps,
  enrollmentOptionFieldProps,
  checkboxFieldProps,
  enrollmentOptionError,
  showAgreeToTermsCheckbox,
  agreeToTermsError,
}: {
  causeName: string;
  scrollerRef?: RefObject<HTMLDivElement>;
  containerProps?: CardProps;
  enrollmentOptionFieldProps?: Omit<RadioGroupProps, 'children' | 'onChange'> & {
    onChange: (value?: boolean) => void;
  };
  checkboxFieldProps: CheckboxProps;
  showAgreeToTermsCheckbox?: boolean;
  enrollmentOptionError?: ReactNode;
  agreeToTermsError?: ReactNode;
}) => {
  return (
    <Card
      className={classes.ShopToGiveEnrollmentForm}
      ref={scrollerRef as MutableRefObject<HTMLDivElement>}
      {...containerProps}
    >
      <Box className={classes.ShopToGiveEnrollmentForm__header}>
        <Text fz="sm" fw="600" c="white">
          Givebacks Rewards Program
        </Text>
      </Box>

      <Stack className={classes.ShopToGiveEnrollmentForm__content}>
        <Text c="gray.8" fw="600">
          Support us every time you shop,{' '}
          <Text span c="cobalt" fw="600">
            at no cost to you.
          </Text>
        </Text>
        <Group wrap="nowrap" gap="sm" align="flex-end">
          <Stack>
            <Text fz="sm" c="gray.8">
              Simply link your card used for this purchase, shop as usual, and over 25,000
              participating merchants & restaurants donate back to{' '}
              <Text span fw="600" fz="inherit">
                {causeName} at no cost to you!
              </Text>
            </Text>
            <Anchor
              className={classes.ShopToGiveEnrollmentForm__learnMoreLink}
              href="https://www.givebacks.com/how-it-works"
              target="_blank"
            >
              Learn More Here
            </Anchor>
          </Stack>
          <Divider size="1px" orientation="vertical" color="cobalt.1" />
          <Box className={classes.ShopToGiveEnrollmentForm__imageContainer}>
            <ImageMantine className={classes.ShopToGiveEnrollmentForm__image} src={image} />
          </Box>
        </Group>
      </Stack>
      <Stack className={classes.ShopToGiveEnrollmentForm__form}>
        <Alert.Collapse type="error" title={enrollmentOptionError} in={!!enrollmentOptionError} />

        <Text fz="sm" fw="600" c="gray.8">
          Select an option:
        </Text>

        <RadioMantine.Group
          {...enrollmentOptionFieldProps}
          value={
            // explicity allow an undefined value so that the radio group doesn't have a default value
            enrollmentOptionFieldProps?.value === undefined
              ? undefined
              : enrollmentOptionFieldProps?.value
                ? 'yes'
                : 'no'
          }
          onChange={(value: string) => {
            enrollmentOptionFieldProps?.onChange?.(value === 'yes');
          }}
          // Don't need error prop here because we have the Alert.Collapse above
          error={undefined}
        >
          <Stack>
            <RadioCard
              bg="mint.1"
              value="yes"
              title={`Support ${causeName} when I shop`}
              description="Agree to Terms & Conditions and link my card to Givebacks to earn free donations"
            />
            <RadioCard value="no" title="No, I don't want to support" />
          </Stack>
        </RadioMantine.Group>

        <Collapse in={!!showAgreeToTermsCheckbox}>
          <ShopToGiveTermsCheckboxCard
            hasError={!!agreeToTermsError}
            labelAppendage=" & link my card to Givebacks to earn free donations"
            checkboxFieldProps={checkboxFieldProps}
          />
        </Collapse>
      </Stack>
    </Card>
  );
};

export { ShopToGiveEnrollmentForm };
